import { useLazyQuery } from '@apollo/client';
import { Empty, Select, Spin } from 'antd';
import { debounce, uniqBy } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { LIMIT, SORT } from '../common/constants';
import { STATES } from './graphql/Queries';
import SelectComponent from './SelectComponent';

const { Option } = Select;

const LOADING_OPTION_KEY = '__loading__';

const StateSelect = (props) => {
  const { isView, countryId, setStateId, userData, ...rest } = props;
  const [stateList, setStateList] = useState([]);
  const [statesSearchTerm, setStatesSearchTerm] = useState('');
  const [isAllStatesFetched, setIsAllStatesFetched] = useState(false);
  const [isStatesAllowClear, setIsStatesAllowClear] = useState(false);
  const [isStatesDropdownVisible, setIsStatesDropdownVisible] = useState(false);
  const [isFetchMoreStatesLoading, setIsFetchMoreStatesLoading] = useState(
    false,
  );
  const listRef = useRef(null);
  const isFetchingMoreRef = useRef(false);

  const [states, { loading: isStatesLoading }] = useLazyQuery(STATES, {
    onCompleted: (response) => {
      if (response?.states?.data?.length < LIMIT) {
        setIsAllStatesFetched(true);
      }
      isFetchingMoreRef.current = false;
      setIsFetchMoreStatesLoading(false);
      if (userData?.state) {
        setStateList(
          uniqBy(
            [
              { name: userData?.state },
              ...stateList,
              ...response?.states?.data,
            ],
            'name',
          ),
        );
        return;
      }
      setStateList([...stateList, ...response?.states?.data]);
    },
    fetchPolicy: 'network-only',
    onError() {
      isFetchingMoreRef.current = false;
      setIsFetchMoreStatesLoading(false);
    },
  });

  useEffect(() => {
    if (countryId) {
      setStatesSearchTerm('');
      setStateList([]);
      states({
        variables: {
          where: {
            countryId,
          },
          filter: {
            limit: LIMIT,
            skip: 0,
            search: statesSearchTerm,
          },
          sort: {
            field: 'name',
            order: SORT?.ASC,
          },
        },
      });
      setIsAllStatesFetched(false);
    }
  }, [isStatesDropdownVisible, countryId]);

  const handleCategoriesScroll = (event) => {
    if (!isAllStatesFetched && !isStatesLoading && !isFetchingMoreRef.current) {
      const target = event?.target;
      const currentLength = stateList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 20
      ) {
        isFetchingMoreRef.current = true;
        setIsFetchMoreStatesLoading(true);
        states({
          variables: {
            where: {
              countryId,
            },
            filter: {
              limit: LIMIT,
              skip: currentLength,
              search: statesSearchTerm,
            },
            sort: {
              field: 'name',
              order: SORT?.ASC,
            },
          },
        });
      }
    }
  };

  const handleCategoriesSearch = (searchTerm) => {
    const trimSearch = searchTerm?.trim();
    setIsAllStatesFetched(false);
    setStateList([]);
    setStatesSearchTerm(trimSearch);
    states({
      variables: {
        where: {
          countryId,
        },
        filter: {
          limit: LIMIT,
          skip: 0,
          search: trimSearch,
        },
        sort: {
          field: 'name',
          order: SORT?.ASC,
        },
      },
    });
  };

  const handleSearchCategoriesDebounce = debounce(handleCategoriesSearch, 500);

  useEffect(() => {
    handleSearchCategoriesDebounce?.cancel();
  }, [handleSearchCategoriesDebounce]);

  useEffect(() => {
    if (isStatesLoading) {
      setIsStatesAllowClear(false);
    } else {
      setIsStatesAllowClear(true);
    }
  }, [isStatesLoading]);

  useEffect(() => {
    if (!isFetchMoreStatesLoading || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [isFetchMoreStatesLoading]);

  return (
    <SelectComponent
      showSearch
      loading={isStatesLoading && isStatesDropdownVisible}
      placeholder="Select State"
      optionFilterProp="children"
      allowClear={isStatesAllowClear}
      onPopupScroll={(event) => {
        listRef.current = event?.target;
        handleCategoriesScroll(event);
      }}
      onSearch={handleSearchCategoriesDebounce}
      onDropdownVisibleChange={(visible) => setIsStatesDropdownVisible(visible)}
      filterOption={false}
      notFoundContent={
        !isFetchMoreStatesLoading && isStatesLoading ? (
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        ) : (
          <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
        )
      }
      disabled={isView || !countryId || userData?.isArchived}
      {...rest}
    >
      {stateList?.map((state) => (
        <Option key={state?.name} value={state?.name}>
          {state?.name}
        </Option>
      ))}
      {isFetchMoreStatesLoading && (
        <Option key={LOADING_OPTION_KEY} value={LOADING_OPTION_KEY} disabled>
          <div className="select-load-more">
            <Spin size="small" />
          </div>
        </Option>
      )}
    </SelectComponent>
  );
};

export default StateSelect;
